/**
 * EstadoCuentaAgencia — Vista consolidada del CxC y CxP de una agencia.
 *
 * Lee de GET /turismo/finanzas/agencias/{id}/estado-cuenta y arma:
 *   - Selector de agencia + fecha de corte
 *   - Resumen ejecutivo: CxC, CxP, neto + a favor de quién
 *   - Aging del CxC en 4 buckets
 *   - Cuenta corriente cronológica con saldos por línea
 *
 * Botones del primer release:
 *   - "Registrar pago": deshabilitado (es el paso #2 del roadmap)
 *   - "Hacer cruce": deep-link al módulo Cruce existente
 *   - "PDF": funciona desde día 1
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Autocomplete, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaymentsIcon from '@mui/icons-material/Payments';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function fmtFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Etiquetas humanas por tipo de movimiento — alineadas con backend
const TIPO_LABEL = {
  reserva_creada:             { label: 'Reserva creada',       color: '#0d9488', bg: '#ccfbf1' },
  voucher_aprobado:           { label: 'Voucher aprobado',     color: '#16a34a', bg: '#dcfce7' },
  pago_comision_anticipada:   { label: 'Comisión retenida',    color: '#7c3aed', bg: '#ede9fe' },
  pago_descuento_comision:    { label: 'Dcto. comisión',       color: '#a855f7', bg: '#f5e9ff' },
  pago_descuento_comercial:   { label: 'Dcto. comercial',      color: '#f97316', bg: '#ffedd5' },
  pago_muelle:                { label: 'Cobro en muelle',      color: '#3b82f6', bg: '#dbeafe' },
  pago_transferencia:         { label: 'Transferencia',        color: '#0ea5e9', bg: '#e0f2fe' },
  pago_efectivo:              { label: 'Efectivo',             color: '#10b981', bg: '#d1fae5' },
  pago_cruce:                 { label: 'Cruce aplicado',       color: '#be185d', bg: '#fce7f3' },
  pago_pago_agencia:          { label: 'Pago de la agencia',   color: '#0ea5e9', bg: '#e0f2fe' },
  pago_compensacion:          { label: 'Compensación',         color: '#be185d', bg: '#fce7f3' },
  pago_manual:                { label: 'Asiento manual',       color: '#64748b', bg: '#f1f5f9' },
  liquidacion_pagada:         { label: 'Liquidación pagada',   color: '#dc2626', bg: '#fee2e2' },
};

export default function EstadoCuentaAgencia() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agenciaPreId = searchParams.get('agencia');
  const empresaId = getEmpresaId();
  const [agencias, setAgencias] = useState([]);
  const [agenciaSel, setAgenciaSel] = useState(null);
  const [fechaCorte, setFechaCorte] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar lista de agencias (multiselect / autocomplete)
  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.agencias || []);
        setAgencias(list);
      })
      .catch(() => setAgencias([]));
  }, [empresaId]);

  // Pre-seleccionar agencia si vino en query (deep-link desde Aging)
  useEffect(() => {
    if (agenciaPreId && agencias.length > 0 && !agenciaSel) {
      const ag = agencias.find(a => String(a.id) === String(agenciaPreId));
      if (ag) setAgenciaSel(ag);
    }
  }, [agenciaPreId, agencias, agenciaSel]);

  const cargar = useCallback(async () => {
    if (!agenciaSel) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/finanzas/agencias/${agenciaSel.id}/estado-cuenta?fecha_corte=${fechaCorte}`,
        { headers: getAuthHeaders() }
      );
      setData(await r.json());
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [agenciaSel, fechaCorte]);

  useEffect(() => { cargar(); }, [cargar]);

  const handlePDF = () => {
    if (!agenciaSel) return;
    // Abrir con auth headers requiere fetch + blob (el endpoint pide token)
    fetch(
      `${API_BASE}/turismo/finanzas/agencias/${agenciaSel.id}/estado-cuenta.pdf?fecha_corte=${fechaCorte}`,
      { headers: getAuthHeaders() }
    )
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      })
      .catch(() => {});
  };

  return (
    <Box>
      {/* Header de la pantalla */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#134e4a', mb: 0.5 }}>
          Estado de cuenta por agencia
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
          Visión consolidada de CxC, CxP, aging y cuenta corriente.
        </Typography>
      </Box>

      {/* Filtros: agencia + fecha corte */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 280 }}
            size="small"
            options={agencias}
            getOptionLabel={(o) => o.nombre || ''}
            value={agenciaSel}
            onChange={(_, v) => setAgenciaSel(v)}
            renderInput={(params) => <TextField {...params} label="Agencia" placeholder="Buscar agencia..." />}
          />
          <TextField
            size="small" type="date" label="Fecha de corte"
            InputLabelProps={{ shrink: true }}
            value={fechaCorte}
            onChange={(e) => setFechaCorte(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <IconButton onClick={cargar} disabled={!agenciaSel} sx={{ bgcolor: '#f1f5f9' }}>
            <RefreshIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          {data && (
            <>
              {/* Lleva al modal de CxC, que es el único lugar donde se
                  aplican pagos: exige comprobante y cuenta de destino. Antes
                  apuntaba a una pantalla que registraba sin evidencia. */}
              <Button startIcon={<PaymentsIcon />} variant="outlined"
                onClick={() => navigate(`/finanzas/cxc?agencia=${agenciaSel.id}`)}
                sx={{ textTransform: 'none', borderColor: '#0d9488', color: '#0d9488' }}>
                Registrar pago
              </Button>
              <Button startIcon={<CompareArrowsIcon />} variant="outlined"
                onClick={() => navigate('/finanzas/cruce')}
                sx={{ textTransform: 'none', borderColor: '#be185d', color: '#be185d' }}>
                Hacer cruce
              </Button>
              <Button startIcon={<PictureAsPdfIcon />} variant="contained"
                onClick={handlePDF}
                sx={{ textTransform: 'none', bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
                PDF
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {!agenciaSel && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            Seleccioná una agencia para ver su estado de cuenta.
          </Typography>
        </Paper>
      )}

      {loading && agenciaSel && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      )}

      {!loading && data && (
        <>
          <ResumenCards resumen={data.resumen} />
          <Aging aging={data.aging_cxc} />
          <CuentaCorriente movimientos={data.movimientos} onAbrirReserva={(id) => navigate(`/booking?reserva=${id}`)} />
        </>
      )}
    </Box>
  );
}

function ResumenCards({ resumen }) {
  const favor = resumen.neto_favor;
  const tituloNeto = favor === 'entretur'
    ? 'Neto a favor de Entretur'
    : favor === 'agencia'
    ? 'Neto a favor de la agencia'
    : 'Balance neto';
  const colorNeto = favor === 'entretur' ? '#0d9488' : favor === 'agencia' ? '#7c3aed' : '#64748b';

  // De qué está hecha la cuenta corriente.
  //
  // Esta tarjeta y la pantalla de CxC daban cifras distintas para la misma
  // agencia —$8.833.000 contra $2.790.000— y no había forma de ver por qué.
  // No es un error de ninguna: la de CxC mide lo que consume CUPO y esta la
  // cuenta corriente entera. El desglose lo hace evidente sin preguntar.
  const partes = [
    { clave: 'cupo', rotulo: 'Consume cupo', valor: resumen.cxc_cupo,
      color: '#0f172a', ayuda: 'Saldo a cargo de la agencia. Es la cifra que aparece en Cuentas por cobrar y la que decide si se le puede seguir vendiendo.' },
    { clave: 'muelle', rotulo: 'Por cobrar en muelle', valor: resumen.cxc_muelle,
      color: '#0369a1', ayuda: 'Saldos que asume el turista y todavía nadie cobró. La agencia los debe hasta que entren, pero no le consumen cupo.' },
    { clave: 'noshow', rotulo: `No-show · ${resumen.num_reservas_no_show || 0}`,
      valor: resumen.cxc_no_show, color: '#b45309',
      ayuda: 'El pasajero no viajó. La lancha salió igual, así que se debe — pero ya no se va a cobrar en el embarcadero.' },
    { clave: 'sincobro', rotulo: 'Sin cobro registrado', valor: resumen.cxc_sin_cobro,
      color: '#b91c1c',
      ayuda: 'Reservas dadas por saldadas cuyos pagos registrados no alcanzan el precio. Es el único bloque que suele ser un error de captura: vale la pena revisarlo.' },
  ].filter((x) => Number(x.valor || 0) > 0.5);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
      gap: 1, mb: 1.5,
    }}>
      <KpiTile
        label="Cuenta corriente"
        value={fmtMoney(resumen.cxc_total)}
        sub="Todo lo facturado menos lo cobrado"
        icon={<ReceiptLongIcon />}
        color="#dc2626"
        footer={partes.length > 1 && (
          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #e2e8f0',
                     display: 'grid', gap: 0.3 }}>
            {partes.map((x) => (
              <Tooltip key={x.clave} title={x.ayuda} arrow placement="right">
                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                           alignItems: 'baseline', gap: 1, cursor: 'help' }}>
                  <Typography sx={{ fontSize: 9.5, color: x.color, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {x.rotulo}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: x.color,
                                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                                    fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                    {fmtMoney(x.valor)}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      />
      <KpiTile
        label="Por pagar"
        value={fmtMoney(resumen.cxp_total)}
        sub="Comisión pendiente"
        icon={<PaymentsIcon />}
        color="#f59e0b"
      />
      <KpiTile
        label={tituloNeto}
        value={fmtMoney(resumen.neto)}
        sub={resumen.ultima_liquidacion
          ? `Últ. liq: ${fmtFecha(resumen.ultima_liquidacion)} · hace ${resumen.dias_sin_liquidar}d`
          : 'Sin liquidaciones'}
        icon={<TrendingUpIcon />}
        color={colorNeto}
      />
    </Box>
  );
}

/**
 * KpiTile — patrón compacto de tarjetas del módulo booking (ProximasPage).
 * Header pill de color, valor grande en monospace, sub-línea gris.
 * Acepta un `footer` opcional para desglose (ej: no-show).
 */
function KpiTile({ label, value, sub, icon, color = '#0891b2', footer }) {
  return (
    <Box sx={{
      position: 'relative',
      p: '10px 14px',
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.3,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 2, background: color, opacity: 0.85,
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
        <Typography sx={{
          fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
          fontWeight: 700, color: '#475569',
        }}>{label}</Typography>
      </Box>
      <Typography sx={{
        fontSize: 18, fontWeight: 800, color: '#0f172a',
        lineHeight: 1.15, letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      }}>{value}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{sub}</Typography>
      )}
      {footer}
    </Box>
  );
}

function Aging({ aging }) {
  const buckets = [
    { key: 'rango_0_30',   label: '0 – 30 días',     color: '#10b981' },
    { key: 'rango_30_60',  label: '30 – 60 días',    color: '#0ea5e9' },
    { key: 'rango_60_90',  label: '60 – 90 días',    color: '#f59e0b' },
    { key: 'rango_90_mas', label: 'Más de 90 días',  color: '#dc2626' },
  ];
  const total = buckets.reduce((acc, b) => acc + (aging[b.key]?.monto || 0), 0);

  if (total <= 0) {
    return (
      <Paper sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <AccessTimeIcon sx={{ fontSize: 14, color: '#0891b2' }} />
          <Typography sx={{ fontSize: 10.5, letterSpacing: '0.08em',
                            textTransform: 'uppercase', fontWeight: 700, color: '#475569' }}>
            Antigüedad del CxC
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          La agencia no tiene cuentas por cobrar pendientes.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 1.25, mb: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <AccessTimeIcon sx={{ fontSize: 14, color: '#0891b2' }} />
        <Typography sx={{ fontSize: 10.5, letterSpacing: '0.08em',
                          textTransform: 'uppercase', fontWeight: 700, color: '#475569' }}>
          Antigüedad del CxC
        </Typography>
      </Box>
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1,
      }}>
        {buckets.map(b => {
          const a = aging[b.key] || { monto: 0, num_reservas: 0 };
          const pct = total > 0 ? (a.monto / total) * 100 : 0;
          return (
            <Box key={b.key} sx={{
              position: 'relative',
              p: '8px 10px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 1.5,
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute', top: 0, left: 0, right: 0,
                height: 2, background: b.color, opacity: 0.85,
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography sx={{ fontSize: 9.5, color: '#64748b', fontWeight: 700,
                                  textTransform: 'uppercase', letterSpacing: '0.06em',
                                  whiteSpace: 'nowrap' }}>
                  {b.label}
                </Typography>
                <Typography sx={{ fontSize: 9, color: '#94a3b8', flexShrink: 0 }}>
                  {a.num_reservas} res
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: 14, fontWeight: 800, color: b.color, lineHeight: 1.15,
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontVariantNumeric: 'tabular-nums', mt: 0.15,
              }}>
                {fmtMoney(a.monto)}
              </Typography>
              <Box sx={{ mt: 0.4, height: 3, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', bgcolor: b.color }} />
              </Box>
              <Typography sx={{ fontSize: 9, color: '#94a3b8', mt: 0.25 }}>
                {pct.toFixed(1)}% del total
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// Sin tildes y en minúsculas: quien busca «sanchez» debe encontrar a
// «Lizeth Sánchez», y quien copia el código de un correo no debería pelear con
// mayúsculas.
const normalizar = (t) => String(t || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

function CuentaCorriente({ movimientos, onAbrirReserva }) {
  const [busqueda, setBusqueda] = useState('');
  // Estabilizado para que el useMemo del filtro no se recalcule en cada render:
  // `movimientos || []` crea un array nuevo cada vez.
  const todos = React.useMemo(() => movimientos || [], [movimientos]);

  // Se busca sobre el código y el nombre del turista, no sobre el concepto
  // entero: buscar en el texto libre hace que «pago» devuelva media tabla.
  const filtrados = React.useMemo(() => {
    const q = normalizar(busqueda).trim();
    if (!q) return todos;
    return todos.filter((m) => normalizar(m.codigo_reserva).includes(q)
      || normalizar(m.nombre_cliente).includes(q));
  }, [todos, busqueda]);

  const hayFiltro = filtrados.length !== todos.length;
  const sinSoporte = filtrados.filter((m) => m.sin_soporte);
  if (!movimientos || movimientos.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
        <Typography sx={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
          Sin movimientos registrados para esta agencia.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <Box sx={{ p: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0',
                 display: 'flex', gap: 1.5, alignItems: 'flex-start',
                 flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#134e4a' }}>
            Cuenta corriente ({hayFiltro
              ? `${filtrados.length} de ${todos.length} movimientos`
              : `${todos.length} movimientos`})
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            Orden cronológico descendente — el más reciente primero. Las celdas en negativo reducen el saldo.
          </Typography>
        </Box>
        <TextField
          size="small" placeholder="Reserva o nombre del turista…"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
          sx={{ width: { xs: '100%', sm: 260 } }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.75 }} />,
            endAdornment: busqueda ? (
              <IconButton size="small" onClick={() => setBusqueda('')} aria-label="Quitar filtro">
                <ClearIcon sx={{ fontSize: 15 }} />
              </IconButton>
            ) : null,
            sx: { fontSize: 12 },
          }} />
      </Box>

      {/* El saldo corriente se acumula sobre TODA la serie, no sobre lo
          filtrado. Se deja así a propósito —es el saldo real de la agencia en
          ese instante— pero hay que decirlo, o dos filas seguidas parecen no
          cuadrar entre ellas. */}
      {hayFiltro && (
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#f0f9ff',
                   borderBottom: '1px solid #bae6fd', display: 'flex',
                   alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`Filtro: ${busqueda}`} onDelete={() => setBusqueda('')}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700,
                      bgcolor: '#0891b2', color: '#fff',
                      '& .MuiChip-deleteIcon': { color: '#e0f2fe', fontSize: 14 } }} />
          <Typography sx={{ fontSize: 11, color: '#0c4a6e' }}>
            Las columnas de saldo siguen siendo las de la agencia completa, no las del filtro.
          </Typography>
        </Box>
      )}

      {/* Pagos de la agencia registrados sin comprobante. El dato ya existía
          —la columna Soporte quedaba en «—»— pero había que ir fila por fila
          para verlo, y una nota que dice «ref 4424» se lee como si hubiera
          respaldo. El conteo arriba lo vuelve accionable. */}
      {sinSoporte.length > 0 && (
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#fffbeb', borderBottom: '1px solid #fde68a',
                   display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: '#b45309' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
            {sinSoporte.length} pago{sinSoporte.length !== 1 ? 's' : ''} sin comprobante
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#a16207' }}>
            — se registró la referencia pero no se adjuntó la evidencia:{' '}
            {sinSoporte.map((m) => m.codigo_reserva).filter(Boolean).join(', ')}
          </Typography>
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Concepto</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">CxC</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">CxP</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Saldo CxC</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Saldo CxP</TableCell>
              <TableCell align="center" sx={{ width: 40, fontWeight: 700, fontSize: 11 }}>Soporte</TableCell>
              <TableCell align="center" sx={{ width: 40 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrados.map((m, idx) => {
              const visual = TIPO_LABEL[m.tipo] || { label: m.tipo, color: '#64748b', bg: '#f1f5f9' };
              const colorCxc = m.monto_cxc > 0 ? '#dc2626' : (m.monto_cxc < 0 ? '#16a34a' : '#94a3b8');
              const colorCxp = m.monto_cxp > 0 ? '#f59e0b' : (m.monto_cxp < 0 ? '#16a34a' : '#94a3b8');
              return (
                <TableRow key={idx} hover>
                  <TableCell sx={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>
                    {fmtFecha(m.fecha)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip size="small" label={visual.label}
                        sx={{ bgcolor: visual.bg, color: visual.color, fontSize: 9.5, fontWeight: 700, height: 18 }} />
                      {/* Badge naranja cuando el movimiento pertenece a una reserva no_show.
                          Da transparencia visual: el operador ve de un vistazo qué eventos
                          contribuyen al CxC no-show que aparece desglosado arriba. */}
                      {/* De qué giro salió este pago. Un pago de agencia
                          puede cubrir varias reservas, así que al mirar UNA
                          reserva la pregunta siguiente es qué más entró en el
                          mismo pago. */}
                      {m.pago_recibido_id && (
                        <Tooltip arrow
                          title={
                            <Box sx={{ fontFamily: 'system-ui', fontSize: 12, maxWidth: 300 }}>
                              <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                                Pago recibido #{m.pago_recibido_id}
                              </Box>
                              {(m.reservas_del_pago || []).length > 1 ? (
                                <>
                                  <Box sx={{ mb: 0.5 }}>
                                    Un solo giro repartido en {m.reservas_del_pago.length} reservas:
                                  </Box>
                                  {m.reservas_del_pago.map((x) => (
                                    <Box key={x.codigo_reserva}
                                      sx={{ display: 'flex', justifyContent: 'space-between',
                                            gap: 1.5,
                                            fontWeight: x.codigo_reserva === m.codigo_reserva ? 700 : 400 }}>
                                      <span>{x.codigo_reserva}</span>
                                      <span>${Number(x.monto).toLocaleString('es-CO')}</span>
                                    </Box>
                                  ))}
                                </>
                              ) : (
                                <Box>Cubrió únicamente esta reserva.</Box>
                              )}
                            </Box>
                          }>
                          <Chip size="small" label={`Pago #${m.pago_recibido_id}`}
                            sx={{ bgcolor: '#e0f2fe', color: '#0369a1', cursor: 'help',
                                  fontSize: 9.5, fontWeight: 700, height: 18,
                                  '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                      )}
                      {m.es_no_show && (
                        <Tooltip title="Reserva quedó como no-show — este movimiento suma al CxC no-show" arrow>
                          <Chip size="small" label="⚠️ No-show"
                            sx={{ bgcolor: '#fef3c7', color: '#92400e',
                                   fontSize: 9.5, fontWeight: 700, height: 18,
                                   '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: '#0f172a' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box component="span" sx={{ minWidth: 0 }}>{m.concepto}</Box>
                      {m.notas && (
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <Box sx={{ fontFamily: 'system-ui', fontSize: 12, whiteSpace: 'pre-wrap', maxWidth: 320 }}>
                              <Box sx={{ fontWeight: 700, mb: 0.5 }}>Notas del pago</Box>
                              {m.notas}
                            </Box>
                          }>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#0891b2', cursor: 'help', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: colorCxc, whiteSpace: 'nowrap' }}>
                    {m.monto_cxc ? (m.monto_cxc > 0 ? '+' : '') + fmtMoney(m.monto_cxc) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: colorCxp, whiteSpace: 'nowrap' }}>
                    {m.monto_cxp ? (m.monto_cxp > 0 ? '+' : '') + fmtMoney(m.monto_cxp) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>
                    {fmtMoney(m.saldo_cxc_despues)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>
                    {fmtMoney(m.saldo_cxp_despues)}
                  </TableCell>
                  <TableCell align="center">
                    {m.comprobante_url ? (
                      <Tooltip title="Ver evidencia (voucher/transferencia)" arrow>
                        <IconButton size="small"
                          href={
                            m.comprobante_url.startsWith('http')
                              ? m.comprobante_url
                              : `${API_BASE}${m.comprobante_url}`
                          }
                          target="_blank" rel="noopener"
                          sx={{ color: '#0891b2' }}>
                          <ReceiptLongIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ) : m.sin_soporte ? (
                      <Tooltip arrow
                        title="Pago registrado sin comprobante — falta adjuntar la evidencia">
                        <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {m.reservacion_id && (
                      <Tooltip title={`Abrir reserva ${m.codigo_reserva}`}>
                        <IconButton size="small" onClick={() => onAbrirReserva(m.reservacion_id)}>
                          <OpenInNewIcon sx={{ fontSize: 14, color: '#64748b' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtrados.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              Ningún movimiento de esta agencia coincide con «{busqueda}».
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Paper>
  );
}
